<template>
  <v-container>
    <v-row v-if="outputNames.length > 1" dense>
      <v-col cols="12">
        <v-select
          :items="outputNames"
          :label="$t('components.CompareSimulationsColumn.kpi')"
          v-model="currentOutput"
          dense
          hide-details
          class="mt-2"
        />
      </v-col>
    </v-row>
    <v-row style="height: 65px;">
      <v-col cols="6">
        <v-chip v-if="simulation.restrictions && Object.keys(simulation.restrictions).length > 0" color="primary" text-color="white">
          <v-avatar left class="primary darken-4">{{ Object.keys(simulation.restrictions).length }}</v-avatar>
          {{ $t('components.CompareSimulationsColumn.restrictions') }}
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon right v-on="on">mdi-chevron-down</v-icon>
            </template>
            <div v-for="(restriction, key) in simulation.restrictions" :key="`restriction_${key}`">
              - {{ $t('components.CompareSimulationsColumn.spendIn') }} <b>{{ prettyMediaNamesDict[channelName(key)] ? prettyMediaNamesDict[channelName(key)] : channelName(key) }}</b> {{ restrictionTypeName(restriction.type) }} {{ formatValue(restriction.firstValue) }} <template v-if="restriction.secondValue && restriction.type === 'between'">{{ $t('components.CompareSimulationsColumn.and') }} {{ formatValue(restriction.secondValue) }}</template>
            </div>
          </v-tooltip>
        </v-chip>
        <v-chip v-if="simulation.time_restrictions && simulation.time_restrictions.length > 0" color="primary" text-color="white">
          <v-avatar left class="primary darken-4">{{ simulation.time_restrictions.length }}</v-avatar>
          {{ $t('components.CompareSimulationsColumn.timeRestrictions') }}
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon right v-on="on">mdi-chevron-down</v-icon>
            </template>
            <div v-for="(restriction, index) in simulation.time_restrictions" :key="`time_restriction_${index}`">
              - {{ $t('components.CompareSimulationsColumn.spendIn') }} <b>{{ timeRestrictionChannelLabel(restriction.channelId) }}</b> {{ restrictionTypeName(restriction.type) }} {{ formatValue(restriction.firstValue) }}<template v-if="restriction.secondValue && restriction.type === 'between'"> {{ $t('components.CompareSimulationsColumn.and') }} {{ formatValue(restriction.secondValue) }}</template><template v-if="restriction.timespan === 'full_period'"> ({{ $t('components.CompareSimulationsColumn.fullPeriod') }})</template><template v-else-if="restriction.timespan === 'custom_range' && restriction.fromDate && restriction.toDate"> ({{ formatDate(restriction.fromDate) }} → {{ formatDate(restriction.toDate) }})</template>
            </div>
          </v-tooltip>
        </v-chip>
        <v-chip v-if="simulation.kpi_weighting && simulation.kpi_weighting.mode !== 'auto'" class="ml-2" color="primary" text-color="white">
          <v-avatar left class="primary darken-4">
            <v-icon small>mdi-weight</v-icon>
          </v-avatar>
          {{ $t('components.CompareSimulationsColumn.kpiWeighting') }}
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon right v-on="on">mdi-chevron-down</v-icon>
            </template>
            <div v-if="simulation.kpi_weighting.weights">
              <div v-for="(weight, key) in visibleKpiWeights" :key="`kpiw_${key}`">
                - <b>{{ key }}</b>: {{ Math.round(weight * 100) }}%
              </div>
            </div>
          </v-tooltip>
        </v-chip>
      </v-col>
      <v-col cols="6">
        <v-card flat class="text-right">
          <v-icon color="primary">mdi-calendar</v-icon>
          {{ formatDate(simulation.date_from) }} → {{ formatDate(simulation.date_to) }}
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="5">
        <text-box-metric :label="`Budget (${spendUnitName})`" :value="budgetDisplay.value" :caption="budgetDisplay.caption" unit="" icon="cash-multiple"/>
      </v-col>
      <v-col cols="7">
        <text-box-metric :label="`Predicted ${customKpiName} (${currentOutputUnitName})`" :value="sales" unit="" icon="chart-line">
          <template v-slot:info v-if="data.precision.sales">
            <percent-chip :value="data.precision.sales" :best="100" icon="mdi-bullseye-arrow"/>
          </template>
        </text-box-metric>
      </v-col>
    </v-row>
    <template v-if="activeSection === 'fullPeriodSummary'">
      <v-row>
        <v-col cols="12">
          <sales-decomposition v-if="salesDecompositionByPeriodData" :dictionary="client.pretty_names ? client.pretty_names.components : null" :data="salesDecompositionByPeriodData" :precision="data.precision.components" :unit="currentOutputUnitName" :custom-kpi-name="customKpiName" :export="true" :export-image-name="exportImageName(`${customKpiName} Decomposition (${currentOutputUnitName})`)" />
          <sales-decomposition v-else :dictionary="client.pretty_names ? client.pretty_names.components : null" :data="currentOutputObject" :precision="data.precision.components" :unit="currentOutputUnitName" :custom-kpi-name="customKpiName" :export="true" :export-image-name="exportImageName(`${customKpiName} Decomposition (${currentOutputUnitName})`)" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <marketing-sales v-if="mediaAttributedDecompositionByPeriodData" :data="mediaAttributedDecompositionByPeriodData" :colors="prettyColors" :precision="data.precision.marketing" :unit="currentOutputUnitName" :custom-kpi-name="customKpiName" :export="true" :export-image-name="exportImageName(`Media ${customKpiName} by Channel (${currentOutputUnitName})`)"/>
          <marketing-sales v-else :data="currentOutputObject" :colors="prettyColors" :precision="data.precision.marketing" :unit="currentOutputUnitName" :custom-kpi-name="customKpiName" :export="true" :export-image-name="exportImageName(`Media ${customKpiName} by Channel (${currentOutputUnitName})`)"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <bar-chart
              :data="dataInvestTimeArrays"
              :title="$t('components.CompareSimulationsColumn.mediaSpendCalendar', { unit: spendUnitName })"
              icon="chart-bell-curve"
              :category-names="[]"
              :x-axis-type-is-date="true"
              :x-axis-rotate="40"
              period="month"
              :show-labels="dataInvestTimeArrays.x.length < 13"
              color="#1d953f"
              :export="true"
              :export-image-name="exportImageName(`Media Spend Calendar (${spendUnitName})`)"
              :grid-bottom-margin="20" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <marketing-spend :data="data.invest" :colors="prettyColors" :unit="spendUnitName" :export="true" :export-image-name="exportImageName(`Media Mix (${spendUnitName})`)"/>
        </v-col>
      </v-row>
      <v-row v-if="roasByChannelData">
        <v-col cols="12">
          <roas-by-channel
            :data="roasByChannelData"
            :colors="prettyColors"
            :unit="roasUnit"
            :custom-kpi-name="customKpiName"
            :export="true"
            :export-image-name="exportImageName(roasUnit ? `ROAS by Channel (${roasUnit})` : 'ROAS by Channel')" />
        </v-col>
      </v-row>
      <v-row v-if="planSaturationData">
        <v-col cols="12">
          <saturation-curves
            :saturation-data="planSaturationData"
            :channel-colors="prettyColors"
            :client="client"
            :custom-kpi-name="customKpiName"
            :output-unit-name="currentOutputUnitName"
            :spend-unit-name="spendUnitName"
            :enable-group-selector="true"
            :compact="true"
            datapoints-label-key="components.SaturationCurves.showSpend" />
        </v-col>
      </v-row>
    </template>
    <template v-if="activeSection === 'monthlyDetailView'">
      <v-row>
        <v-col cols="12">
          <periodic-detail
            key="monthly"
            :client="client"
            :simulation="simulation"
            :data="data"
            period="month"
            :current-output="currentOutput"
            :current-output-unit-name="currentOutputUnitName"
            :spend-unit-name="spendUnitName"
            :colors="prettyColors"
            :export-data="true"
            :hide-legend="true"
            :custom-kpi-name="customKpiName" />
        </v-col>
      </v-row>
    </template>
    <template v-if="activeSection === 'weeklyDetailView'">
      <v-row>
        <v-col cols="12">
          <periodic-detail
            key="weekly"
            :client="client"
            :simulation="simulation"
            :data="data.weekly_detail"
            period="week"
            :current-output="currentOutput"
            :current-output-unit-name="currentOutputUnitName"
            :spend-unit-name="spendUnitName"
            :colors="prettyColors"
            :export-data="true"
            :hide-legend="true"
            :custom-kpi-name="customKpiName" />
        </v-col>
      </v-row>
    </template>
    <template v-if="activeSection === 'touchpoints'">
      <v-row>
        <v-col cols="12">
          <marketing-spend-detail :data="data.invest" :colors="prettyColors" :cols-per-row="12" :sort="false" :unit="spendUnitName" />
        </v-col>
      </v-row>
    </template>
    <template v-if="activeSection === 'segments'">
      <v-row v-if="data.invest.segments">
        <v-col cols="12">
          <marketing-spend-segments-detail :data="data.invest" :colors="prettyColors" :precision="data.precision.segments" :unit="spendUnitName" :half-width="true" :custom-kpi-name="customKpiName" />
        </v-col>
      </v-row>
      <v-row v-if="currentOutputObject.microaudience">
        <v-col cols="12">
          <marketing-sales-by-microaudience :data="currentOutputObject.microaudience" :unit="currentOutputUnitName" :custom-kpi-name="customKpiName" />
        </v-col>
      </v-row>
      <v-row v-if="currentOutputObject.marketing && Object.keys(currentOutputObject.marketing).length > 0 && currentOutputObject.marketing[Object.keys(currentOutputObject.marketing)[0]].segments">
        <v-col cols="12">
          <marketing-sales-detail :data="currentOutputObject" :colors="prettyColors" :precision="data.precision.segments" :cols-per-row="12" :sort="false" :unit="currentOutputUnitName" :custom-kpi-name="customKpiName" />
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import moment from 'moment'
import numbro from 'numbro'

import TextBoxMetric from '@/components/TextBoxMetric.vue'
import MarketingSpend from '@/components/simulationDetails/MarketingSpend.vue'
import MarketingSpendDetail from '@/components/simulationDetails/MarketingSpendDetail.vue'
import SalesDecomposition from '@/components/simulationDetails/SalesDecomposition.vue'
import MarketingSales from '@/components/simulationDetails/MarketingSales.vue'
import MarketingSalesByMicroaudience from '@/components/simulationDetails/MarketingSalesByMicroaudience.vue'
import MarketingSalesDetail from '@/components/simulationDetails/MarketingSalesDetail.vue'
import MarketingSpendSegmentsDetail from '@/components/simulationDetails/MarketingSpendSegmentsDetail.vue'
import SaturationCurves from '@/components/SaturationCurves.vue'
import RoasByChannel from '@/components/simulationDetails/RoasByChannel.vue'
import PercentChip from '@/components/PercentChip.vue'
import BarChart from '@/components/BarChart.vue'
import PeriodicDetail from '@/components/PeriodicDetail.vue'
import { prettyNames } from '@/mixins/prettyNames.js'
import { loadModelReportFromClient } from '@/utils/modelReport.js'
import { isSpendUpTo, investedBudget } from '@/utils/optimizerBudget.js'
import { roasUnitLabel } from '@/utils/roasUnits.js'
import { interpolateCurveValue } from '@/utils/saturationDatapoints.js'
import { hasHiddenKpis, isKpiHidden, firstVisibleOutputName } from '@/utils/hiddenKpis.js'

export default {
  data () {
    return {
      currentOutput: null,
      fetchedModelReport: null,
      fetchedModelReportFor: null
    }
  },
  mixins: [prettyNames],
  computed: {
    referenceModelConfig () {
      if (this.client.plan_reference_model) {
        return this.client.plan_reference_model.config
      } else {
        return this.client.reference_model.config
      }
    },
    prettyMediaNamesDict () {
      return this.client.pretty_names ? this.client.pretty_names.media : {}
    },
    budgetDisplay: function () {
      // For spend_up_to beta plans, simulation.budget is just a ceiling; show the
      // real invested amount as the main figure and the ceiling as a caption.
      const invested = investedBudget(this.simulation)
      if (isSpendUpTo(this.simulation) && invested !== null) {
        return {
          value: invested,
          caption: this.$t('components.CompareSimulationsColumn.upTo', { value: this.formatValue(this.simulation.budget) })
        }
      }
      return { value: this.simulation.budget, caption: '' }
    },
    prettyColors () {
      return this.prettyTranslateNames(this.colors, [''], this.client.pretty_names ? this.client.pretty_names.media : {})
    },
    dataInvestTimeArrays: function () {
      const sortedArray = Object.entries(this.data.invest.time).slice().sort((a, b) => new Date(a[0]) - new Date(b[0]))
      let x = []
      let y = []

      sortedArray.forEach(a => {
        x.push(a[0])
        y.push(a[1])
      })
      return {
        x: x,
        y: y
      }
    },
    data () {
      if ('results' in this.simulation) {
        const basePaths = [
          'invest.mix',
          'invest.media_spend',
          'weekly_detail.invest.media_spend',
          'sales.marketing',
          'sales.media_attributed_sales_decomposition_by_period',
          'weekly_detail.sales.media_attributed_sales_decomposition_by_period'
        ]
        // salesBasePaths and weeklyDetailSalesBasePaths support outputs, and are prettified recursively later
        const salesBasePaths = [
          'marketing',
          'media_attributed_sales_decomposition_by_period'
        ]
        const weeklyDetailSalesBasePaths = [
          'media_attributed_sales_decomposition_by_period'
        ]
        // Include all outputs (recursive versions of the sales basePaths at sales.outputs.output_name)
        let paths = [].concat(basePaths)
        if (this.simulation.results.sales.outputs) {
          const outputNames = Object.keys(this.simulation.results.sales.outputs)
          if (outputNames && outputNames.length > 0) {
            outputNames.forEach(output => {
              if (output !== 'Total') {
                paths = paths.concat(salesBasePaths.map(path => {
                  return `sales.outputs.${output}.${path}`
                }))
                paths = paths.concat(weeklyDetailSalesBasePaths.map(path => {
                  return `weekly_detail.sales.outputs.${output}.${path}`
                }))
              }
            })
          }
        }
        return this.prettyTranslateNames(this.simulation.results, paths, this.client.pretty_names ? this.client.pretty_names.media : {})
        // return this.simulation.results
      } else {
        return null
      }
    },
    outputs () {
      if (this.data && this.data.sales && 'outputs' in this.data.sales) {
        // KPIs hidden by the model's Display settings are left out, and so is the Total while
        // any is hidden (it adds up the hidden KPIs too).
        let outputs = {}
        Object.keys(this.data.sales.outputs).forEach(name => {
          if (!isKpiHidden(this.client, name)) {
            outputs[name] = this.data.sales.outputs[name]
          }
        })
        const metadata = this.referenceModelConfig && this.referenceModelConfig.metadata
        const flagCombinable = !metadata || metadata.combinable_outputs !== false
        const configOutputs = this.referenceModelConfig && this.referenceModelConfig.outputs
        const allAdditive = Object.keys(this.data.sales.outputs).every(name => {
          if (!configOutputs || !configOutputs[name]) return true
          return configOutputs[name].is_additive !== false
        })
        const combinable = flagCombinable && allAdditive
        if ('total' in this.data.sales && combinable && !hasHiddenKpis(this.client)) {
          outputs['Total'] = { ...this.data.sales }
        }
        return outputs
      } else {
        return null
      }
    },
    // The plan's manual KPI weights without the hidden KPIs.
    visibleKpiWeights () {
      const weights = (this.simulation && this.simulation.kpi_weighting && this.simulation.kpi_weighting.weights) || {}
      const visible = {}
      Object.keys(weights).forEach(key => {
        if (!isKpiHidden(this.client, key)) {
          visible[key] = weights[key]
        }
      })
      return visible
    },
    outputNames () {
      if (this.outputs) {
        return Object.keys(this.outputs)
      } else {
        return []
      }
    },
    currentOutputObject () {
      if (this.currentOutput && this.currentOutput !== 'Total') {
        return this.data.sales.outputs[this.currentOutput]
      } else if ('total' in this.data.sales && !(this.data.sales.outputs && hasHiddenKpis(this.client))) {
        return this.data.sales
      } else {
        const firstKey = firstVisibleOutputName(this.client, this.data.sales.outputs)
        return firstKey ? this.data.sales.outputs[firstKey] : { marketing: {} }
      }
    },
    currentOutputUnitName: function () {
      if (!this.currentOutput || this.currentOutput === 'Total') {
        return this.referenceModelConfig.sales_unit_name || 'USD'
      } else if ('outputs' in this.referenceModelConfig) {
        return this.referenceModelConfig.outputs[this.currentOutput].sales_unit_name || 'USD'
      } else {
        return 'USD'
      }
    },
    salesDecompositionAggregationMode () {
      const configOutputs = this.referenceModelConfig && this.referenceModelConfig.outputs
      const isOutputAdditive = (outputName) => {
        if (!configOutputs || !configOutputs[outputName]) return true
        return configOutputs[outputName].is_additive !== false
      }
      const isTotal = !this.currentOutput || this.currentOutput === 'Total'
      if (!isTotal) {
        return isOutputAdditive(this.currentOutput) ? 'sum' : 'average'
      }
      const realOutputs = (this.data && this.data.sales && this.data.sales.outputs)
        ? Object.keys(this.data.sales.outputs)
        : []
      const allAdditive = realOutputs.every(name => isOutputAdditive(name))
      return allAdditive ? 'sum' : 'average'
    },
    salesDecompositionByPeriodData () {
      const obj = this.currentOutputObject
      if (!obj || !obj.sales_decomposition_by_period) {
        return null
      }
      const aggregated = this.aggregateSalesDecompositionByPeriod(obj.sales_decomposition_by_period, this.salesDecompositionAggregationMode)
      let ordered = null
      if (obj.sales_decomposition_total && Array.isArray(obj.sales_decomposition_total.components)) {
        const totalComponents = obj.sales_decomposition_total.components
        const valueByName = {}
        aggregated.components.forEach(c => { valueByName[c.name] = c.value })
        ordered = []
        const seen = {}
        totalComponents.forEach(c => {
          if (c.name in valueByName) {
            ordered.push({ name: c.name, value: valueByName[c.name] })
            seen[c.name] = true
          } else {
            console.log(`Warning: sales decomposition component '${c.name}' present in sales_decomposition_total.components but missing from sales_decomposition_by_period`)
          }
        })
        aggregated.components.forEach(c => {
          if (!seen[c.name]) {
            console.log(`Warning: sales decomposition component '${c.name}' present in sales_decomposition_by_period but missing from sales_decomposition_total.components`)
            ordered.push(c)
          }
        })
      } else {
        ordered = aggregated.components.slice().sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      }
      return { components: ordered, total: aggregated.total }
    },
    mediaAttributedDecompositionByPeriodData () {
      const obj = this.currentOutputObject
      if (!obj || !obj.media_attributed_sales_decomposition_by_period) {
        return null
      }
      const aggregated = this.aggregateSalesDecompositionByPeriod(
        obj.media_attributed_sales_decomposition_by_period,
        this.salesDecompositionAggregationMode
      )
      const marketing = {}
      aggregated.components.forEach(c => {
        marketing[c.name] = { adstock: c.value }
      })
      if (obj.media_attributed_sales_decomposition_total &&
          Array.isArray(obj.media_attributed_sales_decomposition_total.components)) {
        const inByPeriod = new Set(aggregated.components.map(c => c.name))
        obj.media_attributed_sales_decomposition_total.components.forEach(c => {
          if (!inByPeriod.has(c.name)) {
            console.log(`Warning: media component '${c.name}' present in media_attributed_sales_decomposition_total.components but missing from media_attributed_sales_decomposition_by_period`)
          }
        })
        const inTotal = new Set(obj.media_attributed_sales_decomposition_total.components.map(c => c.name))
        aggregated.components.forEach(c => {
          if (!inTotal.has(c.name)) {
            console.log(`Warning: media component '${c.name}' present in media_attributed_sales_decomposition_by_period but missing from media_attributed_sales_decomposition_total.components`)
          }
        })
      }
      return { marketing }
    },
    sales () {
      if (this.salesDecompositionByPeriodData) {
        return this.salesDecompositionByPeriodData.total
      }
      return this.currentOutputObject ? this.currentOutputObject.total : 0
    },
    modelReportFetchKey () {
      // Stable identity of the Storage-backed report version, so the watcher refetches
      // whenever the server uploads a new file (mirrors SimulationDetailsComponent).
      if (!this.client || !this.client.model_report_storage) return null
      const ts = this.client.model_report_updated_at
      let tsKey = ''
      if (ts) {
        if (typeof ts.toMillis === 'function') tsKey = String(ts.toMillis())
        else if (ts.seconds !== undefined) tsKey = ts.seconds + '.' + (ts.nanoseconds || 0)
        else tsKey = String(ts)
      }
      return this.client.model_report_storage + '@' + tsKey
    },
    parsedInlineModelReport () {
      // Legacy inline report (string or object) for clients without Storage-backed reports.
      if (!this.client || this.client.model_report_storage || !this.client.model_report) return null
      if (typeof this.client.model_report === 'string') {
        try {
          return JSON.parse(this.client.model_report)
        } catch (e) {
          console.error('Failed to parse model_report:', e)
          return null
        }
      }
      return this.client.model_report
    },
    // Per-channel saturation curves from the model report, with this simulation's spend
    // overlaid as datapoints (one point per period, sitting on the curve). null when
    // there's no report, no saturation data, or no per-channel spend to plot.
    planSaturationData () {
      if (!this.client || !this.simulation || !this.data) return null
      const report = this.client.model_report_storage ? this.fetchedModelReport : this.parsedInlineModelReport
      if (!report) return null
      // Resolve the saturation object for the currently selected output.
      let saturation = null
      if (report.outputs && this.currentOutput && this.currentOutput in report.outputs && report.outputs[this.currentOutput].saturation) {
        saturation = report.outputs[this.currentOutput].saturation
      } else if (report.saturation) {
        saturation = report.saturation
      } else if (report.outputs) {
        const firstOutput = Object.keys(report.outputs).find(name => !isKpiHidden(this.client, name) && report.outputs[name].saturation)
        saturation = firstOutput ? report.outputs[firstOutput].saturation : null
      }
      if (!saturation) return null
      // Pick the per-channel per-period spend series matching the curve's granularity.
      const granularity = this.client.reference_model && this.client.reference_model.config
        ? this.client.reference_model.config.saturation_granularity
        : null
      let spendSeries = null
      if (granularity === 'W' && this.data.weekly_detail && this.data.weekly_detail.invest && this.data.weekly_detail.invest.media_spend) {
        spendSeries = this.data.weekly_detail.invest.media_spend
      } else if (this.data.invest && this.data.invest.media_spend) {
        spendSeries = this.data.invest.media_spend
      }
      // Overlay the plan's spend on one flat { channel: curve } object.
      const overlaySpend = (channels) => {
        const result = {}
        Object.keys(channels).forEach(channel => {
          const curve = channels[channel]
          const series = spendSeries ? spendSeries[channel] : null
          if (curve && curve.x && series && Array.isArray(series.y)) {
            const datapoints = series.y.map(spend => ({
              x: spend,
              y: interpolateCurveValue(curve.x, curve.y, spend)
            }))
            result[channel] = {
              x: curve.x,
              y: curve.y,
              regions: curve.regions,
              datapoints: datapoints,
              datapoints_dates: series.x
            }
          } else {
            // No spend to overlay for this channel — keep the bare curve.
            result[channel] = curve
          }
        })
        return result
      }
      // Grouped saturation (first-level 'Instant Marketing' sub-key, same detection as the
      // Model Report): translate and overlay the channels inside each group, keeping the
      // group level so SaturationCurves' group selector drives it.
      if (Object.prototype.hasOwnProperty.call(saturation, 'Instant Marketing')) {
        const out = {}
        for (const group in saturation) {
          if (Object.prototype.hasOwnProperty.call(saturation, group)) {
            out[group] = overlaySpend(this.prettyTranslateNames(saturation[group], [''], this.prettyMediaNamesDict))
          }
        }
        return out
      }
      // Translate channel keys so they line up with `prettyColors` and the plan's spend series.
      return overlaySpend(this.prettyTranslateNames(saturation, [''], this.prettyMediaNamesDict))
    },
    // Composite unit of the per-channel ROAS, empty when the selected output and the
    // spend share a unit and the ratio is dimensionless.
    roasUnit () {
      return roasUnitLabel(this.currentOutputUnitName, this.spendUnitName) || ''
    },
    // Per-channel ROAS (mean + lower/upper confidence bounds) from the plan's results
    // JSON (root-level roas_by_channel key). null hides the section — only when the
    // plan carries no such data, since the ratio itself is always meaningful.
    roasByChannelData () {
      if (!this.simulation || !this.simulation.results) return null
      const roas = this.simulation.results.roas_by_channel
      if (!roas || Object.keys(roas).length === 0) return null
      return this.prettyTranslateNames(roas, [''], this.prettyMediaNamesDict)
    }
  },
  watch: {
    modelReportFetchKey: {
      immediate: true,
      handler: function (newKey) {
        if (!newKey) {
          this.fetchedModelReport = null
          this.fetchedModelReportFor = null
          return
        }
        if (newKey === this.fetchedModelReportFor) return
        const target = newKey
        loadModelReportFromClient(this.client)
          .then(report => {
            if (this.modelReportFetchKey === target) {
              this.fetchedModelReport = report
              this.fetchedModelReportFor = target
            }
          })
          .catch(err => {
            console.error('Failed to load model_report from Storage:', err)
            if (this.modelReportFetchKey === target) {
              this.fetchedModelReport = null
              this.fetchedModelReportFor = target
            }
          })
      }
    },
    outputNames: {
      handler (newVal) {
        if (!newVal || newVal.length === 0) {
          this.currentOutput = null
          return
        }
        if (!this.currentOutput || !newVal.includes(this.currentOutput)) {
          if (this.selectedOutput && newVal.includes(this.selectedOutput)) {
            this.currentOutput = this.selectedOutput
          } else if (newVal.includes('Total')) {
            this.currentOutput = 'Total'
          } else {
            this.currentOutput = newVal[0]
          }
        }
      },
      immediate: true
    },
    // Follow the parent-driven selection when it changes, as long as this column actually has
    // that output available.
    selectedOutput (val) {
      if (val && this.outputNames.includes(val)) {
        this.currentOutput = val
      }
    }
  },
  methods: {
    // Name for a chart's exported image, "{model} - {plan} - {chart}" as in the plan
    // details view. The plan name matters here more than there: the two columns show
    // the same charts side by side, so it is what tells the two downloads apart.
    // Missing parts are dropped rather than left as empty segments.
    exportImageName (label) {
      const parts = []
      if (this.client && this.client.name) parts.push(this.client.name)
      if (this.simulation && this.simulation.name) parts.push(this.simulation.name)
      parts.push(label)
      return parts.join(' - ')
    },
    aggregateSalesDecompositionByPeriod (byPeriod, mode = 'sum') {
      if (!byPeriod || typeof byPeriod !== 'object') {
        return { components: [], total: 0 }
      }
      const components = Object.keys(byPeriod).map(name => {
        const ys = (byPeriod[name] && Array.isArray(byPeriod[name].y)) ? byPeriod[name].y : []
        const sum = ys.reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0)
        const value = mode === 'average' && ys.length > 0 ? sum / ys.length : sum
        return { name, value }
      })
      const total = components.reduce((a, c) => a + c.value, 0)
      return { components, total }
    },
    channelName (channel) {
      if (this.client && this.client.channels && channel in this.client.channels) {
        return this.client.channels[channel].name
      } else {
        return channel
      }
    },
    timeRestrictionChannelLabel (channelId) {
      // channelId is an array of channel ids; legacy plans persisted a scalar.
      const ids = Array.isArray(channelId) ? channelId : [channelId]
      return ids.map(id => {
        if (id === '__all__') {
          return this.$t('components.CompareSimulationsColumn.allChannels')
        }
        const name = this.channelName(id)
        return this.prettyMediaNamesDict[name] ? this.prettyMediaNamesDict[name] : name
      }).join(' + ')
    },
    restrictionTypeName: function (type) {
      const types = {
        unrestricted: this.$t('components.CompareSimulationsColumn.unrestricted'),
        more_than: this.$t('components.CompareSimulationsColumn.moreThan'),
        less_than: this.$t('components.CompareSimulationsColumn.lessThan'),
        between: this.$t('components.CompareSimulationsColumn.between'),
        exactly: this.$t('components.CompareSimulationsColumn.exactly')
      }
      return types[type]
    },
    formatDate (date) {
      return moment(date).format('ll')
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    }
  },
  directives: {},
  props: {
    simulation: {
      type: Object,
      required: true
    },
    colors: {
      type: Object,
      required: true
    },
    spendUnitName: {
      type: String,
      required: false,
      default: function () {
        return 'USD'
      }
    },
    client: {
      type: Object,
      required: true
    },
    customKpiName: {
      type: String,
      required: true
    },
    activeSection: {
      type: String,
      required: true
    },
    // Optional: when the parent drives the KPI/output selection (e.g. the What-If Scenario's
    // top-level dropdown), this keeps this column's own output selector in sync with it. When
    // absent the column manages its own selection independently.
    selectedOutput: {
      type: String,
      required: false,
      default: null
    }
  },
  components: {
    TextBoxMetric,
    MarketingSpend,
    MarketingSpendDetail,
    SalesDecomposition,
    MarketingSales,
    MarketingSalesByMicroaudience,
    MarketingSalesDetail,
    MarketingSpendSegmentsDetail,
    PercentChip,
    SaturationCurves,
    RoasByChannel,
    BarChart,
    PeriodicDetail
  }
}
</script>
